import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Alert, Button, Field, Card } from '@evogard/ui';
import type { Group } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { PermissionPicker } from './PermissionPicker.js';

/**
 * Criar ou editar um grupo, marcando o que ele concede.
 *
 * ## As caixas saem do catálogo do servidor, não de uma lista aqui
 *
 * `GET /platform/permissions` devolve recurso × ação declarado por cada
 * módulo. Uma lista escrita nesta tela seria a segunda cópia do catálogo: o
 * módulo novo declararia a permissão dele, o servidor a aceitaria, e a tela
 * continuaria sem oferecê-la — uma permissão que existe e ninguém consegue
 * conceder.
 *
 * ## O que a tela NÃO oferece
 *
 * **Curinga.** `*:*` e `recurso:*` são válidos no servidor e não têm caixa
 * aqui: conceder "tudo, para sempre, inclusive o que ainda não existe" com um
 * clique é o gesto que ninguém revisa depois. Grupo com curinga aparece na
 * lista com a marca dele e continua funcionando; criar um é ato de quem tem
 * acesso ao banco, e é para permanecer assim até alguém decidir o contrário.
 *
 * Isso é decisão desta tela, e por isso está escrita: o servidor aceita, a
 * interface não oferece.
 */
export function GroupEditor({
  group,
  toClose,
}: {
  /** `undefined` cria; um grupo edita. */
  group?: Group;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const [nome, setName] = useState(group?.nome ?? '');
  const [descricao, setDescription] = useState(group?.descricao ?? '');
  const [marked, setMarked] = useState<Set<string>>(new Set(group?.permissions ?? []));
  const [error, setError] = useState<string | null>(null);

  const catalog = useQuery({
    queryKey: ['plataforma', 'catalogo-permissoes'],
    queryFn: async () => {
      const r = await api.platform.catalogPermissions();
      return ok(r, 200, 'Não foi possível carregar o catálogo.');
    },
    // o catálogo é escrito à mão em sete módulos: não muda entre navegações
    staleTime: Infinity,
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        permissions: [...marked],
      };
      const r = group
        ? await api.platform.editGroup({ params: { id: group.id }, body: body })
        : await api.platform.createGroup({ body: body });

      if (r.status !== 200 && r.status !== 201) {
        /*
         * A mensagem do servidor, e não uma genérica: a recusa mais comum aqui
         * é "permissão fora do catálogo", e ela nomeia qual. Trocar por "não
         * foi possível salvar" esconderia justamente o que resolve.
         */
        const bodyError = r.body as { message?: string };
        throw new Error(bodyError.message ?? 'Não foi possível salvar o grupo.');
      }
      return r.body;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['plataforma', 'grupos'] });
      /*
       * As permissões da própria pessoa podem ter mudado — ela pode estar
       * editando o grupo em que está. O contexto é quem manda no que a
       * interface esconde, e ele precisa ser relido.
       */
      void client.invalidateQueries({ queryKey: ['plataforma', 'contexto'] });
      toClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  function send(e: FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  /*
   * O que o grupo concede por curinga e a tela não sabe desenhar. Aparece como
   * aviso, e não some no salvamento: editar um grupo com `*:*` e ver o curinga
   * evaporar porque nenhuma caixa o representava seria a tela apagando poder
   * que ninguém pediu para apagar.
   */
  const wildcards = [...marked].filter((p) => p.includes('*'));

  return (
    <Card title={group ? `Editar ${group.nome}` : 'Novo grupo'}>
      <form onSubmit={send} className="flex flex-col gap-4">
        {error && <Alert tone="erro">{error}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Nome"
            required
            autoFocus
            value={nome}
            onChange={(e) => setName(e.target.value)}
          />
          <Field
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescription(e.target.value)}
            help="O que este grupo faz na prática."
          />
        </div>

        {wildcards.length > 0 && (
          <Alert tone="alerta" title="Este grupo concede por curinga.">
            <p className="mt-1">
              {wildcards.join(', ')} — vale para tudo naquele escopo, inclusive o que
              ainda não existe. As caixas abaixo não representam isso, e salvar mantém o
              curinga como está.
            </p>
          </Alert>
        )}

        {catalog.isLoading && <span className="esqueleto block h-24 w-full" />}

        {catalog.data && (
          <PermissionPicker
            catalog={catalog.data}
            marked={marked}
            onChange={setMarked}
            disabled={save.isPending}
          />
        )}

        <div className="flex gap-2">
          <Button type="submit" loading={save.isPending} disabled={!nome.trim()}>
            {group ? 'Salvar' : 'Criar grupo'}
          </Button>
          <Button type="button" variant="contorno" onClick={toClose}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
